/**
 * ══ the panel's information architecture, in one place ═════════════════════════════════════════════
 *
 * WHY THIS FILE EXISTS, and why it is not inside `AppSidebar.vue` where it used to live.
 *
 * Three surfaces have to agree about where a page lives, and until 2026-09-04 only one of them knew:
 *
 *   · the sidebar        — the row an operator clicks
 *   · the header         — «{group} › {accordion} › {page}», the answer to «where am I» (§14.6)
 *   · the page finder    — `header/SearchBar.vue`, which has to say which section a hit is in (§14.5 N1)
 *
 * A breadcrumb component on 120 pages is 120 places to disagree. One tree, read by three components,
 * cannot disagree with itself. That is the whole argument for this module.
 *
 * ── WHAT THIS FILE DOES NOT CONTAIN: page names ───────────────────────────────────────────────────
 * §14.5 N6 makes `meta.title` the single source of a page's name and the sidebar render
 * `meta.navLabel ?? meta.title`. So a ROW here is a `path` and a permission gate — nothing else. The
 * name comes from the route table at render time, which is what took R13.nav from 22 divergences to
 * structurally zero: there is no second string left to diverge.
 *
 * The Persian literals below are CONTAINER names — groups and accordions. They name a container, not a
 * destination, so no route owns them and they belong here. `scripts/lint-copy.mjs` lists this file in
 * its targets for exactly that reason: the labels moved out of a scanned `.vue` file and the scanner
 * had to move with them, or the rule would have gone quiet on the surface it was written for.
 *
 * ── THE GATES ARE COPIED, NOT REDESIGNED ──────────────────────────────────────────────────────────
 * Every `gate` below is the permission expression that guarded that row in `AppSidebar.vue` before the
 * regroup, transcribed row by row. A restructure that changes which grant reveals which row is a
 * security change wearing a design costume, so it is not enough to have been careful:
 * `navigation.gates.spec.js` mounts the sidebar under the SEVEN REAL BACKEND ROLES plus thirteen
 * single-grant subjects and asserts the visible-path set is byte-identical to
 * `navGates.baseline.json`, which was captured from the pre-rewrite sidebar. Nobody gained a row and
 * nobody lost one.
 *
 * Note the two shapes that look redundant and are not:
 *   · an accordion's own `gate` is an OUTER gate — a child is visible only if BOTH pass.
 *     `/engagement/subscriptions` needs `revenue.read` from its accordion
 *     AND `learners.subscriptions` of its own. Flattening either would hand out a page.
 *   · a group with no `gate` drops out on its own when every child filters away (`buildMenu`), so an
 *     operator never meets a heading standing over nothing.
 *
 * ── LONG GROUPS ───────────────────────────────────────────────────────────────────────────────────
 * Only editorial review and bank quality exceed seven rows. Curriculum, question design, learner
 * reports and operations are now separate jobs rather than long catch-all accordions.
 */
import {
  Activity,
  AlertTriangle,
  Bell,
  BookOpen,
  BrainCircuit,
  ChartNoAxesCombined,
  ClipboardCheck,
  Copy,
  FileQuestion,
  Flag,
  Gauge,
  GraduationCap,
  Headset,
  LayoutDashboard,
  LineChart,
  ListChecks,
  Logs,
  Megaphone,
  Newspaper,
  PenLine,
  School,
  Settings,
  ShieldCheck,
  Target,
  TrendingUp,
  Trophy,
  UserCircle,
  UserCog,
  Users,
  Wallet,
  Wand2,
  Wrench,
  Workflow,
} from 'lucide-vue-next'

/** A row: a destination and the expression that reveals it. The NAME comes from the route (N6). */
const row = (path, gate, badge = null) => ({ path, gate, badge })

/** An accordion: a container name, plus an OUTER gate ANDed onto every child. */
const group = (id, icon, label, gate, children) => ({ id, icon, label, gate, children })

/** A row that sits at the top level rather than inside an accordion. */
const direct = (icon, path, gate, badge = null) => ({ icon, path, gate, badge })

const always = () => true

// Discovery follows the operator's job. Superadmins manage team pay instead of a duplicate
// personal-wallet destination; route/API authority and all financial history remain unchanged.
export const navigationAudienceAllows = (path, can) => path !== '/reviewer-wallet' || !can('system.all')

/**
 * ── the two counts that exist ─────────────────────────────────────────────────────────────────────
 * A row with a number is self-explaining; a row without one is a guess. Two of 117 rows carry one, and
 * §14.5 N9 rules that it STAYS two: `useReviewBacklog.js:44` records that there is no count-only
 * endpoint, so each badge costs a full `limit=1` review payload. Four more badges is four more requests
 * on every page load in every session — a behaviour change, and this pass makes none. The named backend
 * ticket (one aggregate endpoint for every queue the caller can see) is in §14.5 N9.
 */
const REVIEW_BADGE = { source: 'reviewPending', label: 'سؤال منتظر بازبینی' }
const SUPPORT_BADGE = { source: 'waitingForUs', label: 'درخواست منتظر پاسخ' }

export const NAVIGATION = [
  // ── صفحه‌های اصلی ─────────────────────────────────────────────────────────────────────────────
  // نمای کلی در ابتداست تا کاربر تازه‌وارد پیش از صف‌ها و گزارش‌ها یک نقطهٔ شروع روشن ببیند.
  {
    id: 'boards',
    title: null,
    items: [
      direct(LayoutDashboard, '/', (can) => can('dashboard.view')),
      direct(Workflow, '/question-bank/supply-center', (can) => can('questions.read')),
      direct(ListChecks, '/command-center', (can) => can('dashboard.view')),
    ],
  },

  /**
   * ── «کارِ امروز» — the band that answers the ranking problem ──────────────────────────────────
   *
   * Measured: for `content_admin`, 51% of the 68 destinations are pages they will essentially never
   * open, and they carry the same 30px row and the same typography as «صف بازبینی», which has 1,093
   * items in it. For `external_reviewer`, ONE of 31 destinations is their job.
   *
   * Every row here ALSO stays in its accordion. **This band is a shortcut, never a relocation** — a
   * demotion in this panel is a change of RANK, never of REACHABILITY, and `AppSidebar.spec.js`'s
   * zero-orphan assertion stays exactly as strict as it was. The supply center sits under the dashboard; this band keeps six daily shortcuts.
   */
  {
    id: 'today',
    title: 'کارِ امروز',
    pinned: true,
    items: [
      // `questions.review` alone. It was `read && review` until 2026-09-08, when `questions.read` left
      // the reviewer seat — and the AND quietly took this row, the reviewer's own work, off the reviewer's
      // own menu. The page's data load is the claim, which the API gates on `questions.review`.
      direct(ClipboardCheck, '/question-bank/review-queue', (can) => can('questions.review'), REVIEW_BADGE),
      direct(PenLine, '/question-bank/revision-queue', (can) => can('questions.read')),
      direct(Wrench, '/question-bank/content-repair', (can) => can('questions.read')),
      direct(Flag, '/question-bank/reports', (can) => can('questions.read')),
      direct(Copy, '/question-bank/duplicate-holds', (can) => can('questions.read')),
      direct(Headset, '/support', (can) => can('support.read'), SUPPORT_BADGE),
    ],
  },

  // ── «محتوای آموزشی» — curriculum → question design → production → review → quality.
  {
    id: 'content',
    title: 'محتوای آموزشی',
    items: [
      // `questions.read_key` — the bank browse surface leads to the authored key, and `questions.read` is
      // what a blind reviewer holds. See router/index.js and the routes file for the whole argument.
      direct(FileQuestion, '/question-bank/questions', (can) => can('questions.read_key')),

      group('content.author', BookOpen, 'برنامهٔ درسی', (can) => can('curriculum.read'), [
        row('/curriculum/structure', always),
        row('/curriculum/concepts', always),
        row('/curriculum/relationships', always),
        row('/curriculum/graph-audit', always),
        row('/curriculum/versions', always),
        row('/curriculum/exam-windows', always),
        row('/exams/blueprint', (can) => can('questions.read')),
      ]),
      group('content.design', PenLine, 'طراحی سؤال',
        (can) => can('questions.read') || can('questions.blueprint_review') || can('questions.review'), [
          row('/question-bank/blueprints', (can) => can('questions.read') || can('questions.blueprint_review')),
          row('/question-bank/misconceptions', (can) => can('questions.read') || can('questions.review')),
          row('/question-bank/misconception-library', (can) => can('questions.read')),
        ]),

      // تولید — I need MORE questions: what we planned, the act of asking, what came back.
      group('content.produce', Wand2, 'تولید محتوا', (can) => can('questions.read'), [
        row('/question-bank/batches', always),
        row('/question-bank/generate', (can) => can('questions.generate')),
        row('/question-bank/generation-runs', always),
      ]),

      // ── بازبینی و ترمیم — something is WRONG with one, in the order an operator meets it ─────────
      // This accordion was «داوری و ترمیم» until 2026-09-05, and «داوری» is a `blanket: true` LOSER in
      // `utils/vocabulary.js`'s `review` entry — the panel's frozen table for «the act of reviewing»,
      // whose winner is «بازبینی». `npm run lint:copy` reported it at `navigation.js:167`, which is the
      // ONE container label the content lead reads on every scan of their own group, and six of the
      // eight rows under it already say «بازبینی» in their own names. A container that contradicts its
      // own children in the panel's decided vocabulary is the drift §7.2 exists to stop; «داوری» is
      // besides a refereeing/arbitration register that no Konkur content lead uses for editorial work.
      // Renaming it costs a container label and no route, no path and no gate. (§14.12, the cold walk.)
      group('content.judge', ClipboardCheck, 'بازبینی و ترمیم', (can) => can('questions.read') || can('questions.review'), [
        row('/question-bank/review-queue', (can) => can('questions.review'), REVIEW_BADGE),
        // ── `always` meant «whatever the group's gate was», and the group's gate just widened ────────
        // Opening this accordion to `questions.review` (so the reviewer can see their own queue and the
        // rubric) would otherwise have handed them the four rows below it, which are the content team's
        // queues. Each now names the grant it always meant: `questions.read`.
        row('/question-bank/revision-queue', (can) => can('questions.read')),
        // Release sits with the VERBS — judge, rewrite, publish — and not with the two report rows
        // after them. Filing it under the statistics is the burial this page exists to end: its only
        // caller in the whole panel used to be a line inside QuestionCoveragePage.
        row('/question-bank/release', (can) => can('questions.write')),
        row('/question-bank/review-standard', (can) => can('questions.review')),
        row('/question-bank/reports', (can) => can('questions.read')),
        row('/question-bank/duplicate-holds', (can) => can('questions.read')),
        row('/question-bank/content-repair', (can) => can('questions.read')),
        // Both of these are SUPERVISOR reads — queue age across the team, one reviewer's agreement
        // against the cohort's. `questions.review` made «review-ops» the reviewer's own row, which is the
        // one seat it is not for: a contractor being measured does not need the measuring instrument.
        // BOTH grants, not either: on `questions.read` alone this row APPEARS for analytics_admin,
        // content_author and three single-grant boundaries, and a row somebody gains is a leak by the gate
        // spec's own definition. `read ∧ review` is content_admin — the person who runs the programme.
        row('/analytics/review-ops', (can) => can('questions.read')),
        row('/analytics/review-calibration', (can) => can('questions.read')),
      ]),
      direct(Wallet, '/question-bank/reviewer-payments', (can) => can('review_payments.read')),
      direct(Wallet, '/reviewer-wallet', (can) => can('questions.review') && navigationAudienceAllows('/reviewer-wallet', can)),
      direct(Flag, '/access/moderation', (can) => can('moderation.read')),

      // سنجش — is the bank GOOD ENOUGH. Quality → coverage → difficulty, so the pairs read as pairs.
      group('content.measure', Gauge, 'کیفیت و پوشش سؤال', (can) => can('questions.read') || can('curriculum.read'), [
        row('/question-bank/quality', (can) => can('questions.read')),
        row('/question-bank/content-quality', (can) => can('questions.read')),
        row('/analytics/ai-generation-quality', (can) => can('questions.read')),
        row('/question-bank/content-source-comparison', (can) => can('questions.read')),
        row('/question-bank/coverage', (can) => can('questions.read')),
        row('/curriculum/coverage-heatmap', (can) => can('curriculum.read')),
        row('/analytics/content-gap', (can) => can('questions.read')),
        row('/question-bank/calibration', (can) => can('questions.read')),
        row('/analytics/difficulty-drift', (can) => can('questions.read')),
      ]),

    ],
  },

  /** Learner records, learning status, model diagnostics, schools and account lifecycle. */
  {
    id: 'learners',
    title: 'دانش‌آموزان',
    items: [
      group('learners.roster', Users, 'فهرست و مدیریت', (can) => can('learners.read') || can('analytics.read'), [
        row('/learners', (can) => can('learners.read')),
        row('/growth/segments', (can) => can('analytics.read')),
        row('/analytics/learner-follow-up', (can) => can('analytics.read')),
        row('/learners-ops', (can) => can('learners.read') && (can('learners.write') || can('analytics.read'))),
      ]),
      group('learners.schools', School, 'مدرسه‌ها', (can) => schoolManagementEnabled.value && can('schools.read'), [
        row('/engagement/schools', always),
        row('/engagement/school-analytics', always),
        row('/engagement/school-classes', always),
      ]),
      group('learners.learning', GraduationCap, 'وضعیت یادگیری و آمادگی', (can) => can('plm.read'), [
        row('/plm/fsrs-health', always),
        row('/plm/exam-readiness', always),
        row('/plm/weakness-map', always),
        row('/plm/cognitive-performance', always),
        row('/plm/prerequisite-violations', always),
      ]),
      group('learners.plm', BrainCircuit, 'پایش مدل یادگیری', (can) => can('plm.read'), [
        row('/plm/validation', always),
        row('/plm/experiments', (can) => can('plm.read')),
        row('/plm/events', always),
        row('/plm/forecast-audit', always),
      ]),
      group('learners.lifecycle', UserCog, 'حذف حساب', (can) => can('learners.delete_queue'), [
        row('/engagement/deletion-queue', always),
        row('/engagement/deletion-lifecycle', always),
      ]),
    ],
  },

  /**
   * ── «تحلیل و رشد» — learning and business reports, before the actions they inform ─────────────
   * It absorbs the old «رشد و درآمد» group and the analytics rows that were buried inside the 25-row
   * engagement accordion. Every gate is the one the row already carried; nothing here is re-gated.
   */
  {
    id: 'behaviour',
    title: 'تحلیل و رشد',
    items: [
      direct(ChartNoAxesCombined, '/growth/analytics', (can) => can('analytics.read')),
      group('behaviour.progress', TrendingUp, 'فعالیت و پیشرفت یادگیری', (can) => can('analytics.read'), [
        row('/ops/live-sessions', always),
        row('/analytics/growth-trend', always),
        row('/analytics/subject-balance', always),
        row('/analytics/learning-gains', always),
        row('/growth/population', always),
        row('/analytics/goal-progress', always),
        row('/analytics/milestones', always),
      ]),
      group('behaviour.readiness', Target, 'برنامه و تمرین آزمون', (can) => can('analytics.read'), [
        row('/analytics/exam-countdown', always),
      ]),
      group('behaviour.retention', LineChart, 'ماندگاری و ریزش', (can) => can('analytics.read'), [
        row('/analytics/at-risk-learners', always),
        row('/analytics/retention-cohorts', always),
        row('/analytics/churn-timeline', always),
      ]),
      group('behaviour.revenue', Wallet, 'اشتراک و درآمد', (can) => can('revenue.read') || can('analytics.read'), [
        row('/revenue/analytics', (can) => can('revenue.read')),
        row('/analytics/subscription-funnel', (can) => can('analytics.read')),
        row('/engagement/subscriptions', (can) => can('revenue.read') && can('learners.subscriptions')),
        row('/revenue/transactions', (can) => can('revenue.read')),
        row('/revenue/plans', (can) => can('revenue.read')),
        row('/revenue/coupons', (can) => can('revenue.read')),
      ]),
      group('behaviour.growth', Megaphone, 'جذب و بازاریابی', (can) => can('analytics.read'), [
        row('/analytics/campaign-effectiveness', always),
        row('/growth/marketing', always),
        row('/growth/referrals', always),
      ]),
      group('behaviour.product', ChartNoAxesCombined, 'بهبود محصول', (can) => can('experiments.read') || can('analytics.read'), [
        row('/growth/funnel-builder', (can) => can('analytics.read')),
        row('/growth/feedback', (can) => can('analytics.read')),
        row('/growth/experiments', (can) => can('experiments.read')),
      ]),
    ],
  },

  /** Communication and motivation follow the insight that informs them. */
  {
    id: 'engagement',
    title: 'ارتباط و انگیزه',
    items: [
      direct(Headset, '/support', (can) => can('support.read'), SUPPORT_BADGE),
      group('engagement.inapp', Newspaper, 'فید و راهنما', (can) => can('engagement.messages') || can('engagement.faq'), [
        row('/engagement/messages', (can) => can('engagement.messages')),
        row('/faq', (can) => can('engagement.faq')),
      ]),
      group('engagement.notify', Bell, 'اعلان‌ها و ارسال', (can) => can('notifications.read') || can('comms.read'), [
        row('/engagement/notifications', (can) => can('notifications.read')),
        row('/engagement/notification-delivery', (can) => can('notifications.read')),
        row('/engagement/campaigns', (can) => can('notifications.read')),
        row('/engagement/comms-log', (can) => can('comms.read')),
      ]),
      group('engagement.motivation', Trophy, 'رقابت و پاداش', (can) => can('leagues.read') || can('competitions.read') || can('analytics.read') || can('engagement.streak'), [
        row('/engagement/leagues', (can) => can('leagues.read')),
        row('/engagement/competitions', (can) => can('competitions.read')),
        row('/engagement/gamification', (can) => can('analytics.read')),
        row('/engagement/streak-health', (can) => can('analytics.read')),
        row('/engagement/streak-milestones', (can) => can('engagement.streak')),
      ]),
    ],
  },

  /**
   * ── «سامانه» — the bottom of the list, and that is the point ──────────────────────────────────
   * Operations are split into health, failures, and administrative jobs; access and settings follow.
   *
   * The content lead sees eleven SRE screens because their role holds `ops.read`. Ranking them last is
   * all a menu can do about that; the real fix is a permission question for the backend, named in
   * §14.5 N8: does the content lead need `ops.read`?
   */
  {
    id: 'system',
    title: 'مدیریت سامانه',
    items: [
      group('system.health', Activity, 'پایشِ سامانه', (can) => can('ops.read'), [
        row('/ops/health', always),
        row('/ops/queues', always),
        row('/ops/outbox', always),
        row('/ops/schedule', always),
        row('/ops/alerts', always),
        row('/ops/notifications', (can) => can('ops.read')),
        row('/ops/oncall', always),
      ]),
      group('system.errors', AlertTriangle, 'کارایی و خطا', (can) => can('ops.read'), [
        row('/ops/client-errors', always),
        row('/ops/endpoint-latency', always),
        row('/ops/slow-queries', always),
      ]),
      group('system.operations', Logs, 'عملیات و گزارش‌ها', (can) => can('ops.read'), [
        row('/ops/reports', always),
        row('/ops/batch', (can) => can('ops.write')),
      ]),
      // `questions.reviewer_certify` is in this gate for one reason: it is the grant that lets a content
      // lead certify a contracted teacher WITHOUT holding `admin.read`, and `/access/admins` is where
      // that control lives. A grant with no way to reach the screen it authorises is the same stall the
      // backend added the grant to remove. The roster itself stays behind `admin.read`.
      group('system.access', ShieldCheck, 'دسترسی و امنیت',
        (can) => can('admin.read') || can('questions.reviewer_certify') || can('audit.read') || can('security.read')
          || can('approvals.read') || can('compliance.read') || can('moderation.read'), [
          row('/access/admins', (can) => can('admin.read') || can('questions.reviewer_certify')),
          row('/access/roles', (can) => can('admin.read')),
          row('/access/security', (can) => can('security.read')),
          row('/access/ip-allowlist', (can) => can('security.read')),
          row('/access/approvals', (can) => can('approvals.read')),
          row('/access/audit-log', (can) => can('audit.read')),
          row('/access/compliance', (can) => can('compliance.read')),
        ]),
      group('system.settings', Settings, 'تنظیمات و اتصال‌ها',
        (can) => can('settings.read') || can('analytics.read') || can('revenue.read'), [
          row('/settings', (can) => can('settings.read')),
          row('/feature-flags', (can) => can('settings.read')),
          row('/ai-providers', (can) => can('settings.read')),
          row('/ai-cost', (can) => can('settings.read')),
          row('/app-config', (can) => can('settings.read')),
          row('/website', (can) => can('settings.read')),
          row('/integrations', (can) => can('settings.read') || can('analytics.read') || can('revenue.read')),
        ]),
      direct(UserCircle, '/profile', always),
    ],
  },
]

/**
 * Where a path sits, for the header's location line and the finder's result rows.
 *
 * Built from the groups only: the pinned band is a SHORTCUT, so a row that appears in both reports the
 * accordion it actually lives in — otherwise every queue page would announce itself as «کارِ امروز»,
 * which is a band, not a place. Bands with no heading (`title: null`) contribute nothing, so the three
 * boards answer with their own name and no section, which is honest: they are not in a section.
 */
// Keep the original leaf gates, including their former outer gates. Related reports are
// contextual destinations, not extra sidebar rows; none becomes invisible to the finder.
const contentSection = NAVIGATION.find(section => section.id === 'content')
const contentGroups = contentSection.items.filter(item => item.children)
const contentLeaves = new Map()
for (const item of contentSection.items) {
  for (const leaf of item.children || [item]) {
    contentLeaves.set(leaf.path, { ...leaf, gate: can => item.gate(can) && leaf.gate(can) })
  }
}
const cluster = (path, related = []) => ({
  ...contentLeaves.get(path),
  related: related.map(path => contentLeaves.get(path)),
})
const regroup = (id, label, children) => {
  const original = contentGroups.find(item => item.id === id)
  return { ...original, label, gate: always, children }
}
contentSection.items = [
  contentSection.items[0],
  regroup('content.author', 'برنامهٔ درسی', [
    cluster('/curriculum/structure', ['/curriculum/versions']),
    cluster('/curriculum/concepts'),
    cluster('/curriculum/relationships', ['/curriculum/graph-audit']),
    cluster('/curriculum/exam-windows', ['/exams/blueprint']),
  ]),
  regroup('content.design', 'طراحی سؤال', [
    cluster('/question-bank/blueprints'),
    cluster('/question-bank/misconceptions', ['/question-bank/misconception-library']),
  ]),
  regroup('content.produce', 'تولید سؤال', [
    cluster('/question-bank/batches'), cluster('/question-bank/generate'), cluster('/question-bank/generation-runs'),
  ]),
  regroup('content.judge', 'بازبینی و انتشار', [
    cluster('/question-bank/review-queue', ['/question-bank/review-standard', '/analytics/review-ops', '/analytics/review-calibration']),
    cluster('/question-bank/revision-queue'),
    cluster('/question-bank/release'),
    cluster('/question-bank/reports', ['/access/moderation', '/question-bank/duplicate-holds', '/question-bank/content-repair']),
    // Pay belongs with reviewer work. Keep the financial read grant independent of review grants.
    cluster('/question-bank/reviewer-payments'),
  ]),
  // Personal wallet stays discoverable to reviewers; superadmins use the team management page.
  contentLeaves.get('/reviewer-wallet'),
  regroup('content.measure', 'کیفیت و پوشش سؤال', [
    cluster('/question-bank/quality'),
    cluster('/question-bank/coverage', ['/curriculum/coverage-heatmap', '/analytics/content-gap']),
    cluster('/question-bank/content-quality', ['/analytics/ai-generation-quality', '/question-bank/content-source-comparison']),
    cluster('/question-bank/calibration', ['/analytics/difficulty-drift']),
  ]),
]

export const contentContextFor = (path, can) => {
  for (const item of contentSection.items) {
    for (const child of item.children || []) {
      const destinations = [child, ...(child.related || [])]
      if (destinations.some(destination => destination.path === path)) {
        return destinations.filter(destination => destination.gate(can))
      }
    }
  }
  return []
}

const SECTION_BY_PATH = (() => {
  const map = new Map()
  for (const container of NAVIGATION) {
    if (container.pinned) continue
    for (const item of container.items) {
      if (item.children) {
        for (const child of item.children) {
          for (const destination of [child, ...(child.related || [])]) {
            if (!map.has(destination.path)) map.set(destination.path, { group: container.title, accordion: item.label })
          }
        }
      } else if (!map.has(item.path)) {
        map.set(item.path, { group: container.title, accordion: null })
      }
    }
  }

  return map
})()

/**
 * `{ group, accordion }` for a route path, or `null` for one the menu does not carry (a detail page,
 * the login screen, 404). Falls back to the parent path so `/question-bank/questions/AB12` answers with
 * the bank's section rather than with nothing — a detail page IS in the section of the list it came from.
 */
export const sectionOf = (path) => {
  if (!path) return null

  // A band with no heading contributes no section, so the three boards — and anything that walks up to
  // «/» — answer `null` rather than an object full of nulls. They are not in a section, and saying
  // nothing is more honest than inventing a name for the place they are not in.
  const answer = (where) => (where && (where.group || where.accordion) ? where : null)

  if (SECTION_BY_PATH.has(path)) return answer(SECTION_BY_PATH.get(path))

  let candidate = path
  while (candidate.includes('/') && candidate !== '/') {
    candidate = candidate.slice(0, candidate.lastIndexOf('/')) || '/'
    if (SECTION_BY_PATH.has(candidate)) return answer(SECTION_BY_PATH.get(candidate))
  }

  return null
}

/** Every path the tree carries, once, in reading order — the finder's ordering and nothing else. */
export const NAVIGABLE_PATHS = [...SECTION_BY_PATH.keys()]

/**
 * Resolve the tree for one operator.
 *
 * @param can        `usePermission().can` — the SAME function the router's guard reads through.
 * @param labelOf    `(path) => string` — `meta.navLabel ?? meta.title`. Names live in the route table.
 * @param badgeValue `(source) => number|null` — null means «unknown», which renders no badge at all.
 * @param landingPath the path `landingRouteName()` resolves to, so the boards band can put it first.
 *
 * An accordion whose children all filter out is dropped, and a container whose items all drop is
 * dropped with it: an operator never meets a heading standing over nothing.
 */
export function buildMenu({ can, labelOf, badgeValue = () => null, landingPath = null }) {
  const out = []

  for (const container of NAVIGATION) {
    const items = []

    for (const item of container.items) {
      if (!item.gate(can)) continue

      if (item.children) {
        const children = item.children
          .filter((child) => [child, ...(child.related || [])].some(destination => destination.gate(can)))
          .map((child) => ({
            path: [child, ...(child.related || [])].find(destination => destination.gate(can)).path,
            relatedPaths: (child.related || []).filter(destination => destination.gate(can)).map(destination => destination.path),
            name: labelOf(child.gate(can) ? child.path : child.related.find(destination => destination.gate(can)).path),
            badge: child.badge ? { value: badgeValue(child.badge.source), label: child.badge.label } : null,
          }))
        if (children.length) items.push({ id: item.id, icon: item.icon, name: item.label, subItems: children })
        continue
      }

      items.push({
        id: item.path,
        icon: item.icon,
        path: item.path,
        name: labelOf(item.path),
        badge: item.badge ? { value: badgeValue(item.badge.source), label: item.badge.label } : null,
      })
    }

    if (!items.length) continue

    // Where the seat's own landing route IS one of the boards, it is row 1 — see the band's note
    // above. `at === -1` for a seat that lands on a queue, and the declared order stands.
    if (container.id === 'boards' && landingPath) {
      const at = items.findIndex((item) => item.path === landingPath)
      if (at > 0) items.unshift(...items.splice(at, 1))
    }

    out.push({ id: container.id, title: container.title, pinned: Boolean(container.pinned), items })
  }

  return out
}
import { schoolManagementEnabled } from '@/services/adminCapabilities'
